
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Target, Tag, Trash2, AlertCircle, Search, 
  Plus, Calendar, DollarSign, Users, ChevronRight,
  Loader2, Filter, Edit, Building, ArrowUpRight,
  ChevronDown, User, CheckCircle2, X, Download,
  Table, FileText
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
const StatusBadge = ({ status, approvalStatus }) => {
  const getStatusConfig = () => {
    if (status === 'pending_approval') {
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: 'Pending Approval'
      };
    }
    if (status === 'rejected') {
      return {
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Rejected'
      };
    }
    if (status === 'published' && approvalStatus === 'approved') {
      return {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Approved'
      };
    }
    return {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      label: status.charAt(0).toUpperCase() + status.slice(1)
    };
  };

  const config = getStatusConfig();

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
      ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};
const CollegeCampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [approvalLoading, setApprovalLoading] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all',
    date: 'all'
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCampaigns(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Remove the deleted campaign from state
      setCampaigns(prevCampaigns => 
        prevCampaigns.filter(campaign => campaign._id !== id)
      );
      
      // Optional: Show success message
      alert('Campaign deleted successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error deleting campaign';
      setError(errorMsg);
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  const handleApprove = async (campaignId) => {
    try {
      setApprovalLoading(campaignId);
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${campaignId}/approve`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      // Refresh campaigns list
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.message || 'Error approving campaign');
    } finally {
      setApprovalLoading(null);
    }
  };
  
  const handleReject = async (campaignId) => {
    try {
      setApprovalLoading(campaignId);
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${campaignId}/reject`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      // Refresh campaigns list
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.message || 'Error rejecting campaign');
    } finally {
      setApprovalLoading(null);
    }
  };

  const formatAmount = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount}`;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      startup: 'bg-purple-50 text-purple-700 border-purple-100',
      infrastructure: 'bg-blue-50 text-blue-700 border-blue-100',
      scholarship: 'bg-green-50 text-green-700 border-green-100',
      research: 'bg-indigo-50 text-indigo-700 border-indigo-100',
      other: 'bg-gray-50 text-gray-700 border-gray-100'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colors[category] || colors.other} border`}>
        <Tag className="w-3 h-3 mr-1" />
        {category.charAt(0).toUpperCase() + category.slice(1)}
      </span>
    );
  };
  const exportToExcel = () => {
    const exportData = campaigns.map(campaign => ({
      'Title': campaign.title,
      'Category': campaign.category,
      'Status': campaign.status,
      'Target Amount': campaign.financials.targetAmount,
      'Raised Amount': campaign.financials.raisedAmount,
      'Progress': `${((campaign.financials.raisedAmount / campaign.financials.targetAmount) * 100).toFixed(1)}%`,
      'Start Date': new Date(campaign.timeline.startDate).toLocaleDateString(),
      'End Date': new Date(campaign.timeline.endDate).toLocaleDateString(),
      'Organizer Name': `${campaign.organizer.organizedBy.firstName} ${campaign.organizer.organizedBy.lastName}`,
      'Organizer Type': campaign.organizer.type,
      'Donors Count': campaign.donors?.length || 0,
      'Description': campaign.description.short
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Campaigns');
    XLSX.writeFile(wb, 'campaign_list.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text('Campaign Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 23);

    const tableData = campaigns.map(campaign => [
      campaign.title,
      campaign.category,
      formatAmount(campaign.financials.raisedAmount),
      formatAmount(campaign.financials.targetAmount),
      `${((campaign.financials.raisedAmount / campaign.financials.targetAmount) * 100).toFixed(1)}%`,
      new Date(campaign.timeline.startDate).toLocaleDateString(),
      campaign.status
    ]);

    doc.autoTable({
      startY: 30,
      head: [['Title', 'Category', 'Raised', 'Target', 'Progress', 'Start Date', 'Status']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [147, 51, 234] }, // Purple color
      styles: { fontSize: 8, cellPadding: 3 }
    });

    doc.save('campaign_report.pdf');
  };

  // Modify your existing filtered campaigns logic
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                         campaign.description.short.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCategory = filters.category === 'all' || campaign.category === filters.category;
    const matchesStatus = filters.status === 'all' || campaign.status === filters.status;
    
    let matchesDate = true;
    if (filters.date !== 'all') {
      const now = new Date();
      const campaignStart = new Date(campaign.timeline.startDate);
      const campaignEnd = new Date(campaign.timeline.endDate);
      
      switch (filters.date) {
        case 'active':
          matchesDate = campaignStart <= now && campaignEnd >= now;
          break;
        case 'upcoming':
          matchesDate = campaignStart > now;
          break;
        case 'ended':
          matchesDate = campaignEnd < now;
          break;
        default:
          matchesDate = true;
      }
    }

    return matchesSearch && matchesCategory && matchesStatus && matchesDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
          <p className="mt-2 text-gray-500">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage fundraising campaigns and track their progress
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg 
                shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  <button
                    onClick={() => {
                      exportToExcel();
                      setShowExportMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Table className="w-4 h-4 mr-2" />
                    Export to Excel
                  </button>
                  <button
                    onClick={() => {
                      exportToPDF();
                      setShowExportMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export to PDF
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/college/campaigns/create')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg 
              shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Campaign
          </button>
        </div>
      </div>

      {/* Enhanced Filters Section */}
      <div className="mt-8 bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500 appearance-none"
            >
              <option value="all">All Categories</option>
              <option value="startup">Startups</option>
              <option value="infrastructure">Infrastructure</option>
              <option value="scholarship">Scholarships</option>
              <option value="research">Research</option>
              <option value="other">Others</option>
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500 appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="published">Published</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border-gray-200 rounded-lg focus:ring-purple-500 focus:border-purple-500 appearance-none"
            >
              <option value="all">All Dates</option>
              <option value="active">Active Campaigns</option>
              <option value="upcoming">Upcoming Campaigns</option>
              <option value="ended">Ended Campaigns</option>
            </select>
          </div>
        </div>
      </div>

        {/* Table Section */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category & Progress
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timeline
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organizer
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {campaign.media?.images[0] ? (
                            <img
                              src={campaign.media.images[0].url}
                              alt=""
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                              <Target className="h-5 w-5 text-purple-600" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {campaign.title}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {campaign.description.short}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        {getCategoryBadge(campaign.category)}
                        <div className="flex items-center space-x-2">
                          <div className="flex-grow">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>{formatAmount(campaign.financials.raisedAmount)}</span>
                              <span>of {formatAmount(campaign.financials.targetAmount)}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                              <div
                                className="bg-purple-600 h-1.5 rounded-full"
                                style={{ 
                                  width: `${Math.min(
                                    (campaign.financials.raisedAmount / campaign.financials.targetAmount) * 100, 
                                    100
                                  )}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(campaign.timeline.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="text-sm text-gray-500">
                        to {new Date(campaign.timeline.endDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <User className="h-4 w-4 text-purple-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {campaign.organizer.organizedBy.firstName} {campaign.organizer.organizedBy.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {campaign.organizer.type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
  <div className="flex items-center justify-end space-x-3">
    {campaign.organizer.type === 'Alumni' && campaign.status === 'pending_approval' && (
      <>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleApprove(campaign._id);
          }}
          className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 
            rounded-md hover:bg-green-200 transition-colors"
        >
          <CheckCircle2 className="h-4 w-4 mr-1.5" />
          Approve
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleReject(campaign._id);
          }}
          className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-700 
            rounded-md hover:bg-red-200 transition-colors"
        >
          <X className="h-4 w-4 mr-1.5" />
          Reject
        </button>
      </>
    )}
    <button
      onClick={() => navigate(`/college/campaigns/${campaign._id}`)}
      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
    >
      <ChevronRight className="h-5 w-5" />
    </button>
    <button
      onClick={() => navigate(`/college/campaigns/edit/${campaign._id}`)}
      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
    >
      <Edit className="h-5 w-5" />
    </button>
    <button
      onClick={(e) => handleDelete(campaign._id, e)}
      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
    >
      <Trash2 className="h-5 w-5" />
    </button>
  </div>
</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredCampaigns.length === 0 && (
            <div className="text-center py-12">
              <Target className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new campaign.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/college/campaigns/create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Campaign
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollegeCampaignList;
